/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useMemo, useState } from 'react';
import Api from 'entities/helpers/api';
import _ from 'underscore';
import PropTypes from 'prop-types';
import Decorator from 'clientDecorator';
import { LinkButton, ModalBody, Icon } from '@evertrue/et-components';
import ActiveJourneysPills from 'apps/journeys/components/profile/active-journeys-pills';
import ModalTrigger from 'components/modals/modal-trigger';
import Modal from 'components/modals/modal';
import ContactModal from 'apps/profile/components/contact-modal';
import ModalConfirm from 'components/modals/modal-confirm';
import ModalHeaderWithActionsAndImage from './ModalHeaderWithActionsAndImage';
import AvatarEditModal from 'apps/profile/components/avatar-edit-modal';
import AddTaskButton from 'apps/tasks/components/create/add-task-button';
import ProfileActionsMenu from 'apps/profile/components/profile-actions-menu';
import ProfileNav from 'apps/profile/components/profile-nav';
import Avatar from 'components/elements/avatar';
import DialerDrawerButton from 'components/drawer/components/dialer-drawer-button';
import EmailerDrawerButton from 'components/drawer/components/emailer-drawer-button';
import AIEmailTemplate from './ai-email-templates/ai-email-template';
import ContactTimezoneIndicator from 'apps/contact/components/contact-timezone-indicator';
import { useFeatureFlag } from 'entities/helpers/use-feature-flag';
import { useContactModalContext } from '../contexts/contact-modal-context';
import useContactCadences from 'apps/cadences-v2/hooks/use-contact-cadences';
import { isCurrent } from 'apps/cadences-v2/components/contact-cadences/contact-cadences-utils';

const ProfileHeader = ({ contact = {}, tab, onBack, afterProposalSave, userType, setHasActiveJourney }) => {
  const [avatar, setAvatar] = useState(null);
  const [socialProfile, setSocialProfile] = useState({});
  const [phones, setPhones] = useState([]);
  const [showEmailTemplate, setShowEmailTemplate] = useState(false);
  const [timeZoneContact, setTimeZoneContact] = useState({});
  const [activeJourneys, setActiveJourneys] = useState([]);
  const [emailPrompt, setEmailPrompt] = useState({ key: 'no_prompt', display_name: 'Blank Email' });
  const [profileRefreshed, refreshProfile] = useState(false);
  const { openContactModal, closeContactModal } = useContactModalContext();
  const { flagEnabled: hasAI } = useFeatureFlag('aiGeneral');
  const { flagEnabled: hasCadences } = useFeatureFlag('signal');
  const { flagEnabled: hasCadenceV2 } = useFeatureFlag('cadenceV2Internal');

  const { cadences: rawV2Cadences } = useContactCadences(hasCadences && hasCadenceV2 ? contact.id : null);
  const activeV2Cadences = useMemo(
    () =>
      (rawV2Cadences || [])
        // isCurrent() returns true for bulk messages whose cadenceStatus is "draft"
        // (so the cadence owner sees their unsent drafts on the C2.0 tab). On a
        // recipient's profile we only want pills for enrollments that have actually
        // launched, so explicitly exclude draft cadences here.
        .filter((row) => (row.cadenceStatus || '').toLowerCase() !== 'draft' && isCurrent(row))
        .map((row) => ({
          journey_id: row.cadenceId,
          journey_name: row.cadenceName,
          task_source: 'cadence_v2',
          cadenceType: row.cadenceType,
        })),
    [rawV2Cadences]
  );

  const fetchProfiles = () => {
    const gql = String.raw;
    const query = gql`
      query ($id: BigInt!) {
        contact(id: $id) {
          phones {
            phone
            primary
            type
          }
          profile_image {
            url
            type
          }
          contact_attributes {
            name_full
          }
          enrichment {
            social_profiles {
              id
              type
              url
              username
              updated_at
            }
          }
          addresses {
            primary
            time_zone {
              time_zone
              zip_code
              observes_dst
              offset
            }
          }
          active_journeys {
            journey_id
            journey_name
            step_number
            day_number
            owner_user_id
            owner {
              name
            }
            creator {
              name
            }
            current_due_date
          }
        }
      }
    `;

    Api.GRAPHQL.GRAPHQL.post({
      data: JSON.stringify({
        query: String.raw`${query}`,
        variables: { id: contact.id },
      }),
    }).then((resp = {}) => {
      const contact = resp.data.contact || {};
      const active_journeys = contact?.active_journeys || [];
      setActiveJourneys(active_journeys);
      if (active_journeys.length) setHasActiveJourney(true);
      contact.addresses && setTimeZoneContact(contact);
      contact.profile_image && contact.profile_image.url ? setAvatar(contact.profile_image.url) : setAvatar(null);
      contact.profile_image && contact.profile_image.type
        ? setSocialProfile(_.findWhere(contact.enrichment.social_profiles, { type: contact.profile_image.type }))
        : setSocialProfile({});
      contact.phones && setPhones(contact.phones);
    });
  };

  useEffect(() => {
    fetchProfiles();
  }, [profileRefreshed, contact.id]);

  useEffect(() => {
    if (activeV2Cadences.length) setHasActiveJourney(true);
  }, [activeV2Cadences.length]);

  const deceased = Decorator.Profile.getDeceased(contact);
  const name = Decorator.Contacts.getDetailedName(contact, { include_middle: true, include_prefix: true });
  const remote_id = _.find(contact.identities, { type: 'remote_id' });

  const handleOpenEmailTemplate = ({ key, display_name }) => {
    closeContactModal();
    setTimeout(() => {
      setShowEmailTemplate(true);
    }, [300]);
    setEmailPrompt({ key, display_name });
  };

  const renderAvatarEditModal = () => {
    return (
      <Modal width={450}>
        <ModalConfirm header="Remove Profile Image">
          <AvatarEditModal
            contact={contact}
            imgUrl={avatar}
            socialProfile={socialProfile}
            onBlackList={fetchProfiles}
          />
        </ModalConfirm>
      </Modal>
    );
  };

  const renderEmailTemplateModal = () => {
    const title = emailPrompt.display_name;
    return (
      <Modal width={818} keepOpen>
        <ModalHeaderWithActionsAndImage
          title={title}
          submitLabel="Done"
          closeLabel="Back"
          imageSrc="/images/ai-email.png"
          closeModal={() => {
            setShowEmailTemplate(false);
            setTimeout(() => {
              openContactModal(true);
            }, [300]);
          }}
          onSubmit={() => setShowEmailTemplate(false)}
        />
        <ModalBody>
          <AIEmailTemplate title={title} contact={contact} promptKey={emailPrompt.key} />
        </ModalBody>
      </Modal>
    );
  };

  return (
    <div className="profile-header">
      <div style={{ display: 'flex' }}>
        <LinkButton title="Go back" className="profile-header--back" onClick={onBack}>
          <Icon icon="chevron-left" size={2} />
        </LinkButton>
        <div style={{ width: '100%' }}>
          {avatar ? (
            <ModalTrigger modal={renderAvatarEditModal()} title={`Edit ${name}'s Avatar Images`} c>
              <div className="profile-header--avatar">
                <Avatar url={avatar} className="fs-exclude-data" name={name} hasDelete={true} />
              </div>
            </ModalTrigger>
          ) : (
            <div className="profile-header--avatar">
              <Avatar url={avatar} className="fs-exclude-data" name={name} />
            </div>
          )}

          <div className="profile-header--user">
            <div className="profile-header--name-wrapper">
              <h1 className={`profile-header--name ${deceased ? 'is-deceased' : ''}`}>
                <span className="fs-exclude-data" style={{ display: 'flex' }}>
                  {name} <ContactTimezoneIndicator contact={timeZoneContact} />
                </span>
              </h1>
              {deceased ? <span className="profile-header--deceased">{deceased}</span> : null}
              <div className="profile-header--role">
                <span className="fs-exclude-data">{Decorator.Profile.getRoleWithYear(contact)}</span>
              </div>
              {remote_id && remote_id?.value ? (
                <div className="profile-header--role">Remote Id: {remote_id?.value}</div>
              ) : null}
              {hasCadences && (!!activeJourneys.length || !!activeV2Cadences.length) && (
                <ActiveJourneysPills
                  activeJourneys={activeJourneys}
                  activeV2Cadences={activeV2Cadences}
                  contactName={contact.name}
                />
              )}
            </div>
          </div>
          <div className="pull-right profile-header--buttons">
            {/* close actions when other is active */}
            <DialerDrawerButton name={name} contact={contact} phones={phones} />
            <EmailerDrawerButton name={name} contact={contact} />
            <AddTaskButton buttonType="icon-btn" contact={contact} primary={false} userType={userType} />
            <ContactModal contact={contact} hasAI={hasAI} handleOpenEmailTemplate={handleOpenEmailTemplate} />
            <ProfileActionsMenu
              contact={contact}
              afterProposalSave={afterProposalSave}
              activeJourneys={activeJourneys}
              refreshProfile={refreshProfile}
            />
          </div>
        </div>
      </div>
      <ProfileNav id={contact.id} tab={tab} />
      <ModalTrigger modal={renderEmailTemplateModal()} show={showEmailTemplate} title="AI generated email templates" />
    </div>
  );
};

ProfileHeader.propTypes = {
  contact: PropTypes.object,
  tab: PropTypes.string,
  onBack: PropTypes.func,
  afterProposalSave: PropTypes.func,
  userType: PropTypes.string,
  setHasActiveJourney: PropTypes.func,
};

export default ProfileHeader;
